/**
 * CSV Parser for Transaction Imports
 * Handles various CSV formats from banks and financial institutions
 */

/**
 * Parse CSV file to transaction objects
 * @param {File} file - CSV file from upload
 * @returns {Promise<Array>} Array of transaction objects
 */
export const parseTransactionCSV = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          reject(new Error('CSV file appears to be empty or invalid'));
          return;
        }

        // Parse header
        const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());

        // Validate required columns
        const columnIndices = findColumnIndices(headers);

        // Build helpful error message if columns are missing
        const missingColumns = [];
        if (columnIndices.date === null) missingColumns.push('Date');
        if (columnIndices.description === null) missingColumns.push('Description/Name/Merchant');
        if (columnIndices.amount === null) missingColumns.push('Amount');

        if (missingColumns.length > 0) {
          const detectedHeaders = headers.join(', ');
          reject(new Error(
            `Could not find required columns: ${missingColumns.join(', ')}. ` +
            `Found headers: ${detectedHeaders}. ` +
            `Please ensure your CSV has columns for date, merchant/payee name, and amount.`
          ));
          return;
        }

        // Parse rows
        const transactions = [];
        const errors = [];

        for (let i = 1; i < lines.length; i++) {
          try {
            const values = parseCSVLine(lines[i]);

            if (values.length < 3) continue; // Skip invalid rows

            const dateStr = values[columnIndices.date];
            const description = values[columnIndices.description];

            // Handle split debit/credit columns or single amount column
            let amountStr;
            let amount;

            if (columnIndices._useSplitAmount) {
              // Bank uses separate debit/credit columns
              const debitStr = columnIndices._debitCol !== null ? values[columnIndices._debitCol] : '';
              const creditStr = columnIndices._creditCol !== null ? values[columnIndices._creditCol] : '';

              const debitAmount = parseAmount(debitStr);
              const creditAmount = parseAmount(creditStr);

              // Debit is money out (negative), credit is money in (positive)
              if (!isNaN(debitAmount) && debitAmount !== 0) {
                amount = -Math.abs(debitAmount); // Ensure debit is negative
                amountStr = debitStr;
              } else if (!isNaN(creditAmount) && creditAmount !== 0) {
                amount = Math.abs(creditAmount); // Ensure credit is positive
                amountStr = creditStr;
              } else {
                // Skip rows with no amount in either column
                continue;
              }
            } else {
              amountStr = values[columnIndices.amount];
              amount = parseAmount(amountStr);
            }

            // Skip empty rows
            if (!dateStr || !description) continue;

            // Parse date (support multiple formats)
            const date = parseDate(dateStr);
            if (!date) {
              errors.push(`Row ${i + 1}: Invalid date "${dateStr}"`);
              continue;
            }

            // Validate amount
            if (isNaN(amount)) {
              errors.push(`Row ${i + 1}: Invalid amount "${amountStr}"`);
              continue;
            }

            transactions.push({
              date: date,
              description: description.trim(),
              amount: amount,
              categoryId: null, // To be categorized
              categorizedBy: null,
              rawData: {
                dateStr,
                amountStr
              }
            });
          } catch (error) {
            errors.push(`Row ${i + 1}: ${error.message}`);
          }
        }

        if (transactions.length === 0) {
          reject(new Error('No valid transactions found in CSV'));
          return;
        }

        resolve({ transactions, errors });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Detect bank format from headers
 * @param {Array} headers - Array of header strings (lowercase)
 * @returns {Object} Bank format info { bank: string, mapping: Object }
 */
const detectBankFormat = (headers) => {
  const headerSet = new Set(headers.map(h => h.trim()));
  const headerStr = headers.join(',');

  // Monzo format: has 'name' column and specific columns like 'emoji', 'local_amount'
  // Monzo uses 'name' for merchant and 'description' for reference numbers
  if ((headerSet.has('name') && (headerSet.has('emoji') || headerSet.has('local_amount') || headerSet.has('local_currency'))) ||
      (headerSet.has('name') && headerSet.has('notes and #tags'))) {
    return {
      bank: 'monzo',
      descriptionColumn: 'name', // Monzo: 'name' contains the merchant
      dateColumn: headerSet.has('date') ? 'date' : 'created'
    };
  }

  // Starling format: has 'counter party' column
  if (headerSet.has('counter party') || headerSet.has('counterparty')) {
    return {
      bank: 'starling',
      descriptionColumn: headerSet.has('counter party') ? 'counter party' : 'counterparty'
    };
  }

  // Revolut format: has 'completed date' and 'description'
  if (headerSet.has('completed date') || (headerSet.has('type') && headerSet.has('product'))) {
    return {
      bank: 'revolut',
      descriptionColumn: 'description',
      dateColumn: headerSet.has('completed date') ? 'completed date' : 'started date'
    };
  }

  // Chase format: has 'transaction description'
  if (headerSet.has('transaction description')) {
    return {
      bank: 'chase',
      descriptionColumn: 'transaction description'
    };
  }

  // Generic format - use priority-based detection
  return { bank: 'generic', descriptionColumn: null };
};

/**
 * Find column indices for required fields with smart priority
 * @param {Array} headers - Array of header strings
 * @returns {Object} Column indices
 */
const findColumnIndices = (headers) => {
  const indices = {
    date: null,
    description: null,
    amount: null,
    debit: null,     // For banks with separate debit/credit columns
    credit: null,
    moneyOut: null,  // Alternative names for debit/credit
    moneyIn: null,
    paidOut: null,
    paidIn: null
  };

  // Detect bank format first
  const bankFormat = detectBankFormat(headers);

  // Define column priorities (higher priority = preferred)
  // For description: merchant/payee names are better than generic 'description'
  // because 'description' often contains reference numbers in some bank formats
  const descriptionPriority = {
    'merchant': 100,
    'merchant name': 100,
    'payee': 90,
    'payee name': 90,
    'counter party': 85,
    'counterparty': 85,
    'name': 80,           // Monzo uses 'name' for merchant
    'transaction description': 75,
    'details': 70,
    'narrative': 65,
    'description': 60,    // Lower priority - often contains references
    'memo': 50,
    'reference': 40       // Lowest - usually transaction IDs
  };

  const datePriority = {
    'date': 100,
    'transaction date': 95,
    'trans date': 90,
    'posting date': 85,
    'completed date': 80,
    'started date': 75,
    'created': 70,
    'value date': 65
  };

  let bestDateScore = -1;
  let bestDescScore = -1;

  headers.forEach((header, index) => {
    const h = header.trim().toLowerCase();

    // Check date columns
    for (const [pattern, score] of Object.entries(datePriority)) {
      if (h === pattern || h.includes(pattern)) {
        // If bank format specifies a date column, boost its score
        const adjustedScore = (bankFormat.dateColumn && h.includes(bankFormat.dateColumn))
          ? score + 50
          : score;
        if (adjustedScore > bestDateScore) {
          bestDateScore = adjustedScore;
          indices.date = index;
        }
      }
    }

    // Check description columns
    for (const [pattern, score] of Object.entries(descriptionPriority)) {
      if (h === pattern || (h.includes(pattern) && !h.includes('date'))) {
        // If bank format specifies a description column, boost its score significantly
        const adjustedScore = (bankFormat.descriptionColumn && h === bankFormat.descriptionColumn)
          ? score + 100  // Strong boost for bank-specific column
          : score;
        if (adjustedScore > bestDescScore) {
          bestDescScore = adjustedScore;
          indices.description = index;
        }
      }
    }

    // Check amount columns - prefer single 'amount' column
    if (h === 'amount' || h === 'transaction amount' || h === 'value') {
      indices.amount = index;
    }

    // Check for separate debit/credit columns (many UK banks use these)
    if (h === 'debit' || h === 'debit amount') indices.debit = index;
    if (h === 'credit' || h === 'credit amount') indices.credit = index;
    if (h === 'money out') indices.moneyOut = index;
    if (h === 'money in') indices.moneyIn = index;
    if (h === 'paid out') indices.paidOut = index;
    if (h === 'paid in') indices.paidIn = index;
  });

  // If no single amount column but we have debit/credit columns, mark as using split columns
  if (indices.amount === null) {
    if (indices.debit !== null || indices.credit !== null) {
      indices._useSplitAmount = true;
      indices._debitCol = indices.debit;
      indices._creditCol = indices.credit;
    } else if (indices.moneyOut !== null || indices.moneyIn !== null) {
      indices._useSplitAmount = true;
      indices._debitCol = indices.moneyOut;
      indices._creditCol = indices.moneyIn;
    } else if (indices.paidOut !== null || indices.paidIn !== null) {
      indices._useSplitAmount = true;
      indices._debitCol = indices.paidOut;
      indices._creditCol = indices.paidIn;
    }

    // Set amount to debit column as fallback for validation
    if (indices._useSplitAmount) {
      indices.amount = indices._debitCol ?? indices._creditCol;
    }
  }

  // Store detected bank format for logging/debugging
  indices._bankFormat = bankFormat.bank;

  return indices;
};

/**
 * Parse CSV line handling quoted values and commas within quotes
 * @param {string} line - CSV line
 * @returns {Array} Array of values
 */
const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  values.push(current);

  return values.map(v => v.trim().replace(/^"|"$/g, ''));
};

/**
 * Parse date from various formats
 * @param {string} dateStr - Date string
 * @returns {Date|null} Parsed date or null
 */
const parseDate = (dateStr) => {
  if (!dateStr) return null;

  // Clean up the date string
  const cleanDateStr = dateStr.trim();

  // Handle timestamp formats (e.g., "2024-01-15 10:30:00 +0000" from Monzo)
  // Extract just the date portion if it's a full timestamp
  const timestampMatch = cleanDateStr.match(/^(\d{4}[-\/]\d{2}[-\/]\d{2})/);
  if (timestampMatch) {
    const date = new Date(timestampMatch[1]);
    if (!isNaN(date) && date.getFullYear() > 2000) return date;
  }

  // Try ISO format (2024-01-15 or 2024/01/15)
  let date = new Date(cleanDateStr);
  if (!isNaN(date) && date.getFullYear() > 2000) return date;

  // Try UK format (15/01/2024 or 15-01-2024) - day first
  const ukMatch = cleanDateStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (ukMatch) {
    const [, first, second, year] = ukMatch;
    // Determine if it's UK (DD/MM) or US (MM/DD) format
    // If first number > 12, it must be a day (UK format)
    // If second number > 12, first must be month (US format)
    // Otherwise, assume UK format for UK banking app
    if (parseInt(first) > 12) {
      // First is day (UK format: DD/MM/YYYY)
      date = new Date(year, parseInt(second) - 1, parseInt(first));
    } else if (parseInt(second) > 12) {
      // Second is day (US format: MM/DD/YYYY)
      date = new Date(year, parseInt(first) - 1, parseInt(second));
    } else {
      // Ambiguous - assume UK format (DD/MM/YYYY) as this is a UK-focused app
      date = new Date(year, parseInt(second) - 1, parseInt(first));
    }
    if (!isNaN(date)) return date;
  }

  // Try short year format (DD/MM/YY)
  const shortYearMatch = cleanDateStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/);
  if (shortYearMatch) {
    const [, first, second, year] = shortYearMatch;
    const fullYear = parseInt(year) > 50 ? 1900 + parseInt(year) : 2000 + parseInt(year);
    // Assume UK format (DD/MM/YY)
    if (parseInt(first) > 12) {
      date = new Date(fullYear, parseInt(second) - 1, parseInt(first));
    } else if (parseInt(second) > 12) {
      date = new Date(fullYear, parseInt(first) - 1, parseInt(second));
    } else {
      date = new Date(fullYear, parseInt(second) - 1, parseInt(first));
    }
    if (!isNaN(date)) return date;
  }

  // Try parsing various other formats that JavaScript Date might understand
  // This handles formats like "15 Jan 2024", "January 15, 2024", etc.
  date = new Date(cleanDateStr);
  if (!isNaN(date) && date.getFullYear() >= 2000 && date.getFullYear() <= 2100) {
    return date;
  }

  return null;
};

/**
 * Parse amount removing currency symbols and formatting
 * @param {string} amountStr - Amount string
 * @returns {number} Parsed amount
 */
const parseAmount = (amountStr) => {
  if (!amountStr) return NaN;

  // Remove currency symbols, spaces, and commas
  let cleaned = amountStr.replace(/[£$€,\s]/g, '');

  // Handle parentheses as negative (accounting format)
  if (cleaned.match(/^\(.*\)$/)) {
    cleaned = '-' + cleaned.replace(/[()]/g, '');
  }

  // Handle explicit positive sign
  cleaned = cleaned.replace(/^\+/, '');

  return parseFloat(cleaned);
};

/**
 * Validate parsed transactions
 * @param {Array} transactions - Array of parsed transactions
 * @returns {Object} { errors, warnings, isValid }
 */
export const validateTransactions = (transactions) => {
  const errors = [];
  const warnings = [];

  transactions.forEach((txn, idx) => {
    // Check date range (not in future, not too old)
    const now = new Date();
    if (txn.date > now) {
      warnings.push(`Row ${idx + 2}: Future date ${txn.date.toLocaleDateString()}`);
    }

    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
    if (txn.date < threeYearsAgo) {
      warnings.push(`Row ${idx + 2}: Date more than 3 years old`);
    }

    // Check amount magnitude
    if (Math.abs(txn.amount) > 1000000) {
      warnings.push(`Row ${idx + 2}: Unusually large amount ${txn.amount}`);
    }

    if (txn.amount === 0) {
      warnings.push(`Row ${idx + 2}: Zero amount`);
    }

    // Check description
    if (!txn.description || txn.description.length < 2) {
      errors.push(`Row ${idx + 2}: Invalid description`);
    }
  });

  return {
    errors,
    warnings,
    isValid: errors.length === 0
  };
};

/**
 * Detect CSV format and suggest mapping
 * @param {string} csvText - CSV content
 * @returns {Object} Detected format info
 */
export const detectCSVFormat = (csvText) => {
  const lines = csvText.split('\n').filter(l => l.trim());
  if (lines.length === 0) return null;

  const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
  const headerSet = new Set(headers);

  // Bank-specific format detection
  const bankFormats = {
    monzo: {
      indicators: ['name', 'emoji', 'local_amount', 'notes and #tags'],
      requiredMatch: 2,
      description: 'Monzo Bank'
    },
    starling: {
      indicators: ['counter party', 'counterparty', 'reference', 'balance'],
      requiredMatch: 2,
      description: 'Starling Bank'
    },
    revolut: {
      indicators: ['completed date', 'started date', 'product', 'fee'],
      requiredMatch: 2,
      description: 'Revolut'
    },
    barclays: {
      indicators: ['number', 'date', 'account', 'amount', 'subcategory'],
      requiredMatch: 3,
      description: 'Barclays'
    },
    nationwide: {
      indicators: ['date', 'transaction type', 'description', 'paid out', 'paid in'],
      requiredMatch: 3,
      description: 'Nationwide'
    },
    hsbc: {
      indicators: ['date', 'description', 'amount'],
      requiredMatch: 3,
      description: 'HSBC or Standard Format'
    },
    chase: {
      indicators: ['transaction date', 'transaction description', 'amount'],
      requiredMatch: 2,
      description: 'Chase'
    }
  };

  // Check bank-specific formats
  for (const [bankName, config] of Object.entries(bankFormats)) {
    const matches = config.indicators.filter(ind =>
      headers.some(h => h.includes(ind) || h === ind)
    );
    if (matches.length >= config.requiredMatch) {
      return {
        format: bankName,
        bankDescription: config.description,
        confidence: (matches.length / config.indicators.length) * 100,
        headers,
        matchedColumns: matches
      };
    }
  }

  // Generic format detection
  const formats = {
    standard: ['date', 'description', 'amount'],
    bank: ['transaction date', 'description', 'debit', 'credit'],
    export: ['date', 'payee', 'amount', 'balance']
  };

  for (const [formatName, requiredHeaders] of Object.entries(formats)) {
    const matches = requiredHeaders.filter(req =>
      headers.some(h => h.includes(req))
    );

    if (matches.length >= 2) {
      return {
        format: formatName,
        confidence: (matches.length / requiredHeaders.length) * 100,
        headers
      };
    }
  }

  return {
    format: 'unknown',
    confidence: 0,
    headers
  };
};

/**
 * Group transactions by month
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Transactions grouped by month
 */
export const groupTransactionsByMonth = (transactions) => {
  const grouped = {};

  transactions.forEach(txn => {
    const monthKey = `${txn.date.getFullYear()}-${String(txn.date.getMonth() + 1).padStart(2, '0')}`;

    if (!grouped[monthKey]) {
      grouped[monthKey] = [];
    }

    grouped[monthKey].push(txn);
  });

  return grouped;
};

/**
 * Calculate transaction statistics
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Statistics
 */
export const calculateTransactionStats = (transactions) => {
  const income = transactions.filter(t => t.amount > 0);
  const expenses = transactions.filter(t => t.amount < 0);

  return {
    total: transactions.length,
    incomeCount: income.length,
    expenseCount: expenses.length,
    totalIncome: income.reduce((sum, t) => sum + t.amount, 0),
    totalExpenses: Math.abs(expenses.reduce((sum, t) => sum + t.amount, 0)),
    avgTransaction: transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0) / transactions.length,
    dateRange: {
      earliest: new Date(Math.min(...transactions.map(t => t.date))),
      latest: new Date(Math.max(...transactions.map(t => t.date)))
    }
  };
};

/**
 * Export transactions to CSV format
 * @param {Array} transactions - Array of transactions
 * @param {Object} categories - Category lookup
 * @returns {string} CSV string
 */
export const exportTransactionsToCSV = (transactions, categories = {}) => {
  const headers = ['Date', 'Description', 'Amount', 'Category'];
  const rows = [headers];

  transactions.forEach(txn => {
    const category = categories[txn.categoryId];
    rows.push([
      txn.date.toISOString().split('T')[0],
      `"${txn.description.replace(/"/g, '""')}"`, // Escape quotes
      txn.amount.toFixed(2),
      category ? category.name : 'Uncategorized'
    ]);
  });

  return rows.map(row => row.join(',')).join('\n');
};
